import { faroCoreApiClient } from "@api/faro-core-api-client";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { Banner, Text, useToast } from "@flat-ui";
import { useAppSelector } from "@store/store-helper";
import { loggedInUserSelector } from "@store/user/user-selector";
import { useCallback, useEffect, useState } from "react";

/** Display state of the resend verification banner */
type BannerDisplayState = "hidden" | "emailNotConfirmed";

/** Renders the dialog to remind users to verify their email address or to request a new verification email */
export function ResendVerification(): JSX.Element | null {
  const { openToast } = useToast();
  const { handleErrorWithToast } = useErrorContext();
  const loggedInUser = useAppSelector(loggedInUserSelector);
  const [bannerDisplayState, setBannerDisplayState] =
    useState<BannerDisplayState>("hidden");

  /** Determines the banner display sate */
  useEffect(() => {
    if (loggedInUser && !loggedInUser.isMailVerified) {
      setBannerDisplayState("emailNotConfirmed");
    }
  }, [loggedInUser]);

  /** Send verification url through email */
  const resendVerificationUrl = useCallback(async () => {
    // Immediately hide the banner to prevent multiple clicks
    setBannerDisplayState("hidden");
    if (loggedInUser) {
      try {
        await faroCoreApiClient.users.resendVerificationMail(
          loggedInUser.email
        );

        openToast({
          title: "New verification mail was sent to your mail address",
        });
      } catch (error) {
        handleErrorWithToast({
          id: `resendVerificationEmail-${Date.now().toString()}`,
          title: "Error resending verification email",
          error,
        });
        // If it fails to resend the email show the banner again since the problem was not solved.
        setBannerDisplayState("emailNotConfirmed");
      }
    }
  }, [handleErrorWithToast, loggedInUser, openToast]);

  /** Early return if the banner shouldn't show */
  if (bannerDisplayState === "hidden") {
    return null;
  }

  return (
    <Banner
      variant="warning"
      title="Unverified email"
      onClose={() => setBannerDisplayState("hidden")}
      action={{
        label: "Resend verification email",
        action: resendVerificationUrl,
      }}
    >
      <Text variant="bodyM">
        To continue using Sphere XG, verify your email address. Didn’t get an
        email?
      </Text>
    </Banner>
  );
}
